/**
 * FormError — displays a form-level error message (not field-level).
 *
 * Surfaces errors from the root of the RHF errors object, or accepts an
 * explicit `message` prop for API-level / server-returned errors.
 *
 * Rule 8: used to surface submit errors that are not field-validation errors
 * (e.g. network failure, server rejection). Pair with sonner for toast-level
 * errors; use this for inline form-level copy.
 *
 * See: CLAUDE.md Rule 8
 */

import React from "react";
import { useFormContext } from "react-hook-form";

export interface FormErrorProps {
  /**
   * Explicit error message to display. If omitted, reads from
   * RHF's `root.serverError` (or `root`) field.
   */
  message?: string;
  className?: string;
}

export function FormError({ message, className }: FormErrorProps): React.JSX.Element | null {
  const {
    formState: { errors },
  } = useFormContext();

  const derivedMessage =
    message ??
    // RHF convention: setError("root.serverError", ...) for server-side errors
    (errors.root as { serverError?: { message?: string } } | undefined)?.serverError?.message ??
    (errors.root as { message?: string } | undefined)?.message;

  if (!derivedMessage) return null;

  return (
    <p role="alert" className={className} data-form-error>
      {derivedMessage}
    </p>
  );
}
